import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert, Badge, Button, Card, Col, Container, Form, Modal, Nav, Row, Spinner, Tab, Table
} from 'react-bootstrap'
import { useNavigate, useParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { EMPLOYMENT_TYPES, LEAVE_STATUS, STAFF_STATUS, UPLOAD_BASE, fmtDateTH, fmtDays } from './hrShared'

const emptyProfile = {
  hp_national_id: '', hp_address: '', hp_education_level: '', hp_hire_date: '',
  hp_employment_type: '', hp_department: '', hp_position_title: '',
  hp_emergency_name: '', hp_emergency_phone: '', hp_emergency_relation: '',
  hp_status: 'active', hp_resign_date: '', hp_note: '',
}

export default function HrStaffDetail() {
  const { tId } = useParams()
  const navigate = useNavigate()

  const [detail, setDetail] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // profile form
  const [profile, setProfile] = useState(emptyProfile)
  const [savingProfile, setSavingProfile] = useState(false)

  // education / work-history modals
  const [eduModal, setEduModal] = useState(null)   // null | {he_id?, ...}
  const [workModal, setWorkModal] = useState(null) // null | {hw_id?, ...}
  const [savingItem, setSavingItem] = useState(false)

  // document upload
  const [docForm, setDocForm] = useState({ hd_type: '', hd_title: '', file: null })
  const [uploading, setUploading] = useState(false)

  // leave history
  const [leaveHistory, setLeaveHistory] = useState([])

  const fetchDetail = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await axiosInstance.get(`/api/hr/staff/${tId}`)
      if (res.data.success) {
        const d = res.data.data
        setDetail(d)
        setProfile({
          ...emptyProfile,
          ...Object.fromEntries(Object.keys(emptyProfile).map(k => [k, d[k] ?? ''])),
        })
      }
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลบุคลากรไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [tId])

  const fetchLeaveHistory = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/api/hr/leave-requests?tId=${tId}`)
      if (res.data.success) setLeaveHistory(res.data.data)
    } catch (_) { /* non-critical */ }
  }, [tId])

  useEffect(() => { fetchDetail(); fetchLeaveHistory() }, [fetchDetail, fetchLeaveHistory])

  const handleSaveProfile = async (e) => {
    e.preventDefault()
    setSavingProfile(true)
    try {
      const res = await axiosInstance.put(`/api/hr/staff/${tId}/profile`, profile)
      if (res.data.success) {
        Swal.fire({ icon: 'success', title: 'บันทึกข้อมูลแล้ว', timer: 1500, showConfirmButton: false })
        fetchDetail()
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'บันทึกไม่สำเร็จ', text: err.response?.data?.message || '' })
    } finally {
      setSavingProfile(false)
    }
  }

  const handleSaveEducation = async (e) => {
    e.preventDefault()
    setSavingItem(true)
    try {
      await axiosInstance.post('/api/hr/education', { ...eduModal, tb_teacher_t_id: Number(tId) })
      setEduModal(null)
      fetchDetail()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'บันทึกไม่สำเร็จ', text: err.response?.data?.message || '' })
    } finally {
      setSavingItem(false)
    }
  }

  const handleSaveWork = async (e) => {
    e.preventDefault()
    setSavingItem(true)
    try {
      await axiosInstance.post('/api/hr/work-history', { ...workModal, tb_teacher_t_id: Number(tId) })
      setWorkModal(null)
      fetchDetail()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'บันทึกไม่สำเร็จ', text: err.response?.data?.message || '' })
    } finally {
      setSavingItem(false)
    }
  }

  const confirmDelete = async (title, url) => {
    const result = await Swal.fire({
      icon: 'warning', title, text: 'การลบไม่สามารถย้อนกลับได้',
      showCancelButton: true, confirmButtonText: 'ลบ', cancelButtonText: 'ยกเลิก',
      confirmButtonColor: '#dc3545',
    })
    if (!result.isConfirmed) return
    try {
      await axiosInstance.delete(url)
      fetchDetail()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'ลบไม่สำเร็จ', text: err.response?.data?.message || '' })
    }
  }

  const handleUploadDocument = async (e) => {
    e.preventDefault()
    if (!docForm.file) return
    setUploading(true)
    try {
      const fd = new FormData()
      fd.append('hd_type', docForm.hd_type)
      fd.append('hd_title', docForm.hd_title)
      fd.append('document', docForm.file)
      await axiosInstance.post(`/api/hr/staff/${tId}/documents`, fd)
      setDocForm({ hd_type: '', hd_title: '', file: null })
      e.target.reset()
      fetchDetail()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'อัปโหลดไม่สำเร็จ', text: err.response?.data?.message || '' })
    } finally {
      setUploading(false)
    }
  }

  const st = STAFF_STATUS[detail?.hp_status] || STAFF_STATUS.active

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between mb-4">
            <h4 className="main-title mb-0">
              <i className="ri-user-3-line me-2 text-primary" />
              ประวัติบุคลากร{detail ? `: ${detail.t_name}` : ''}
            </h4>
            <Button variant="outline-secondary" size="sm" onClick={() => navigate('/hr/staff')}>
              <i className="ri-arrow-left-line me-1" />กลับ
            </Button>
          </div>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : detail && (
            <>
              <Card className="card-one mb-3">
                <Card.Body className="d-flex flex-wrap align-items-center gap-3">
                  <div>
                    <h5 className="mb-1">{detail.t_name}</h5>
                    <div className="text-muted fs-sm">
                      รหัสประจำตัว <code>{detail.t_idnumber}</code>
                      {detail.t_phone && <> · โทร {detail.t_phone}</>}
                    </div>
                    <div className="mt-1">
                      {(detail.positions || []).map(p => (
                        <Badge bg="light" text="dark" className="me-1 border" key={p.pos_id}>{p.pos_name}</Badge>
                      ))}
                    </div>
                  </div>
                  <div className="ms-auto text-end">
                    <Badge bg={st.bg} className="fs-sm">{st.label}</Badge>
                    {detail.hp_department && <div className="text-muted fs-sm mt-1">{detail.hp_department}</div>}
                  </div>
                </Card.Body>
              </Card>

              <Tab.Container defaultActiveKey="profile">
                <Card className="card-one">
                  <Card.Header>
                    <Nav variant="tabs" className="card-header-tabs">
                      <Nav.Item><Nav.Link eventKey="profile">ประวัติส่วนตัว</Nav.Link></Nav.Item>
                      <Nav.Item><Nav.Link eventKey="education">การศึกษา</Nav.Link></Nav.Item>
                      <Nav.Item><Nav.Link eventKey="work">ประวัติการทำงาน</Nav.Link></Nav.Item>
                      <Nav.Item><Nav.Link eventKey="documents">เอกสาร</Nav.Link></Nav.Item>
                      <Nav.Item><Nav.Link eventKey="leave">ประวัติการลา</Nav.Link></Nav.Item>
                    </Nav>
                  </Card.Header>
                  <Card.Body>
                    <Tab.Content>
                      {/* ---- Profile ---- */}
                      <Tab.Pane eventKey="profile">
                        <Form onSubmit={handleSaveProfile}>
                          <Row className="g-3">
                            <Col md={4}>
                              <Form.Label className="fs-sm mb-1">เลขบัตรประชาชน</Form.Label>
                              <Form.Control size="sm" maxLength={13} value={profile.hp_national_id || ''}
                                onChange={e => setProfile(p => ({ ...p, hp_national_id: e.target.value }))} />
                            </Col>
                            <Col md={4}>
                              <Form.Label className="fs-sm mb-1">วุฒิการศึกษาสูงสุด</Form.Label>
                              <Form.Control size="sm" value={profile.hp_education_level || ''}
                                onChange={e => setProfile(p => ({ ...p, hp_education_level: e.target.value }))} />
                            </Col>
                            <Col md={4}>
                              <Form.Label className="fs-sm mb-1">วันที่เริ่มงาน</Form.Label>
                              <Form.Control size="sm" type="date" value={profile.hp_hire_date || ''}
                                onChange={e => setProfile(p => ({ ...p, hp_hire_date: e.target.value }))} />
                            </Col>
                            <Col md={4}>
                              <Form.Label className="fs-sm mb-1">ประเภทการจ้าง</Form.Label>
                              <Form.Select size="sm" value={profile.hp_employment_type || ''}
                                onChange={e => setProfile(p => ({ ...p, hp_employment_type: e.target.value }))}>
                                <option value="">— เลือก —</option>
                                {Object.entries(EMPLOYMENT_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                              </Form.Select>
                            </Col>
                            <Col md={4}>
                              <Form.Label className="fs-sm mb-1">ฝ่าย/แผนก</Form.Label>
                              <Form.Control size="sm" value={profile.hp_department || ''}
                                onChange={e => setProfile(p => ({ ...p, hp_department: e.target.value }))} />
                            </Col>
                            <Col md={4}>
                              <Form.Label className="fs-sm mb-1">ตำแหน่ง</Form.Label>
                              <Form.Control size="sm" value={profile.hp_position_title || ''}
                                onChange={e => setProfile(p => ({ ...p, hp_position_title: e.target.value }))} />
                            </Col>
                            <Col md={12}>
                              <Form.Label className="fs-sm mb-1">ที่อยู่</Form.Label>
                              <Form.Control size="sm" as="textarea" rows={2} value={profile.hp_address || ''}
                                onChange={e => setProfile(p => ({ ...p, hp_address: e.target.value }))} />
                            </Col>
                            <Col md={4}>
                              <Form.Label className="fs-sm mb-1">ผู้ติดต่อฉุกเฉิน</Form.Label>
                              <Form.Control size="sm" value={profile.hp_emergency_name || ''}
                                onChange={e => setProfile(p => ({ ...p, hp_emergency_name: e.target.value }))} />
                            </Col>
                            <Col md={4}>
                              <Form.Label className="fs-sm mb-1">เบอร์โทรฉุกเฉิน</Form.Label>
                              <Form.Control size="sm" value={profile.hp_emergency_phone || ''}
                                onChange={e => setProfile(p => ({ ...p, hp_emergency_phone: e.target.value }))} />
                            </Col>
                            <Col md={4}>
                              <Form.Label className="fs-sm mb-1">ความสัมพันธ์</Form.Label>
                              <Form.Control size="sm" value={profile.hp_emergency_relation || ''}
                                onChange={e => setProfile(p => ({ ...p, hp_emergency_relation: e.target.value }))} />
                            </Col>
                            <Col md={4}>
                              <Form.Label className="fs-sm mb-1">สถานะ</Form.Label>
                              <Form.Select size="sm" value={profile.hp_status || 'active'}
                                onChange={e => setProfile(p => ({ ...p, hp_status: e.target.value }))}>
                                {Object.entries(STAFF_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                              </Form.Select>
                            </Col>
                            {(profile.hp_status === 'resigned' || profile.hp_status === 'retired') && (
                              <Col md={4}>
                                <Form.Label className="fs-sm mb-1">วันที่พ้นสภาพ</Form.Label>
                                <Form.Control size="sm" type="date" value={profile.hp_resign_date || ''}
                                  onChange={e => setProfile(p => ({ ...p, hp_resign_date: e.target.value }))} />
                              </Col>
                            )}
                            <Col md={12}>
                              <Form.Label className="fs-sm mb-1">หมายเหตุ</Form.Label>
                              <Form.Control size="sm" as="textarea" rows={2} value={profile.hp_note || ''}
                                onChange={e => setProfile(p => ({ ...p, hp_note: e.target.value }))} />
                            </Col>
                          </Row>
                          <div className="mt-3 text-end">
                            <Button type="submit" size="sm" variant="primary" disabled={savingProfile}>
                              {savingProfile ? <Spinner size="sm" className="me-1" /> : <i className="ri-save-line me-1" />}
                              บันทึกข้อมูล
                            </Button>
                          </div>
                        </Form>
                      </Tab.Pane>

                      {/* ---- Education ---- */}
                      <Tab.Pane eventKey="education">
                        <div className="text-end mb-2">
                          <Button size="sm" variant="primary" onClick={() => setEduModal({})}>
                            <i className="ri-add-line me-1" />เพิ่มวุฒิการศึกษา
                          </Button>
                        </div>
                        <Table responsive hover className="mb-0 fs-sm">
                          <thead className="table-light">
                            <tr>
                              <th>ระดับ</th><th>วุฒิ/ปริญญา</th><th>สาขา</th><th>สถาบัน</th><th>ปีที่จบ (พ.ศ.)</th><th style={{ width: 90 }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {(detail.education || []).length === 0 ? (
                              <tr><td colSpan={6} className="text-center text-muted py-4">ยังไม่มีข้อมูลการศึกษา</td></tr>
                            ) : detail.education.map(ed => (
                              <tr key={ed.he_id}>
                                <td>{ed.he_level || '-'}</td>
                                <td>{ed.he_degree || '-'}</td>
                                <td>{ed.he_major || '-'}</td>
                                <td>{ed.he_institution || '-'}</td>
                                <td>{ed.he_grad_year || '-'}</td>
                                <td className="text-end">
                                  <Button variant="link" size="sm" className="p-0 me-2" onClick={() => setEduModal(ed)}>
                                    <i className="ri-edit-line" />
                                  </Button>
                                  <Button variant="link" size="sm" className="p-0 text-danger"
                                    onClick={() => confirmDelete('ลบข้อมูลการศึกษา?', `/api/hr/education/${ed.he_id}`)}>
                                    <i className="ri-delete-bin-line" />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Tab.Pane>

                      {/* ---- Work history ---- */}
                      <Tab.Pane eventKey="work">
                        <div className="text-end mb-2">
                          <Button size="sm" variant="primary" onClick={() => setWorkModal({})}>
                            <i className="ri-add-line me-1" />เพิ่มประวัติการทำงาน
                          </Button>
                        </div>
                        <Table responsive hover className="mb-0 fs-sm">
                          <thead className="table-light">
                            <tr>
                              <th>ตำแหน่ง</th><th>หน่วยงาน</th><th>เริ่ม</th><th>สิ้นสุด</th><th>หมายเหตุ</th><th style={{ width: 90 }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {(detail.workHistory || []).length === 0 ? (
                              <tr><td colSpan={6} className="text-center text-muted py-4">ยังไม่มีประวัติการทำงาน</td></tr>
                            ) : detail.workHistory.map(w => (
                              <tr key={w.hw_id}>
                                <td>{w.hw_position || '-'}</td>
                                <td>{w.hw_organization || '-'}</td>
                                <td>{fmtDateTH(w.hw_start_date)}</td>
                                <td>{w.hw_end_date ? fmtDateTH(w.hw_end_date) : 'ปัจจุบัน'}</td>
                                <td>{w.hw_note || '-'}</td>
                                <td className="text-end">
                                  <Button variant="link" size="sm" className="p-0 me-2" onClick={() => setWorkModal(w)}>
                                    <i className="ri-edit-line" />
                                  </Button>
                                  <Button variant="link" size="sm" className="p-0 text-danger"
                                    onClick={() => confirmDelete('ลบประวัติการทำงาน?', `/api/hr/work-history/${w.hw_id}`)}>
                                    <i className="ri-delete-bin-line" />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Tab.Pane>

                      {/* ---- Documents ---- */}
                      <Tab.Pane eventKey="documents">
                        <Form onSubmit={handleUploadDocument} className="border rounded p-3 mb-3">
                          <Row className="g-2 align-items-end">
                            <Col md={3}>
                              <Form.Label className="fs-sm mb-1">ประเภทเอกสาร</Form.Label>
                              <Form.Control size="sm" placeholder="เช่น สำเนาวุฒิ, สัญญาจ้าง" value={docForm.hd_type}
                                onChange={e => setDocForm(f => ({ ...f, hd_type: e.target.value }))} />
                            </Col>
                            <Col md={4}>
                              <Form.Label className="fs-sm mb-1">ชื่อเอกสาร</Form.Label>
                              <Form.Control size="sm" required value={docForm.hd_title}
                                onChange={e => setDocForm(f => ({ ...f, hd_title: e.target.value }))} />
                            </Col>
                            <Col md={3}>
                              <Form.Label className="fs-sm mb-1">ไฟล์ (PDF/Word/รูปภาพ ≤ 10MB)</Form.Label>
                              <Form.Control size="sm" type="file" required accept=".pdf,.doc,.docx,image/*"
                                onChange={e => setDocForm(f => ({ ...f, file: e.target.files[0] || null }))} />
                            </Col>
                            <Col md={2}>
                              <Button type="submit" size="sm" variant="primary" className="w-100" disabled={uploading}>
                                {uploading ? <Spinner size="sm" /> : <><i className="ri-upload-2-line me-1" />อัปโหลด</>}
                              </Button>
                            </Col>
                          </Row>
                        </Form>
                        <Table responsive hover className="mb-0 fs-sm">
                          <thead className="table-light">
                            <tr>
                              <th>ประเภท</th><th>ชื่อเอกสาร</th><th>ไฟล์เดิม</th><th>อัปโหลดเมื่อ</th><th>โดย</th><th style={{ width: 90 }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {(detail.documents || []).length === 0 ? (
                              <tr><td colSpan={6} className="text-center text-muted py-4">ยังไม่มีเอกสาร</td></tr>
                            ) : detail.documents.map(d => (
                              <tr key={d.hd_id}>
                                <td>{d.hd_type || '-'}</td>
                                <td>{d.hd_title}</td>
                                <td className="text-truncate" style={{ maxWidth: 180 }}>{d.hd_original_name || '-'}</td>
                                <td>{fmtDateTH(d.created_at)}</td>
                                <td>{d.uploaded_by_name || '-'}</td>
                                <td className="text-end">
                                  <a
                                    className="me-2"
                                    href={`${UPLOAD_BASE}/uploads/hr_documents/${d.hd_filename}`}
                                    target="_blank" rel="noreferrer" title="เปิดไฟล์"
                                  >
                                    <i className="ri-download-2-line" />
                                  </a>
                                  <Button variant="link" size="sm" className="p-0 text-danger"
                                    onClick={() => confirmDelete('ลบเอกสารนี้?', `/api/hr/documents/${d.hd_id}`)}>
                                    <i className="ri-delete-bin-line" />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Tab.Pane>

                      {/* ---- Leave history ---- */}
                      <Tab.Pane eventKey="leave">
                        <Table responsive hover className="mb-0 fs-sm">
                          <thead className="table-light">
                            <tr>
                              <th>ประเภท</th><th>วันที่ลา</th><th className="text-end">จำนวนวัน</th><th>เหตุผล</th><th>สถานะ</th><th>ผู้อนุมัติ</th>
                            </tr>
                          </thead>
                          <tbody>
                            {leaveHistory.length === 0 ? (
                              <tr><td colSpan={6} className="text-center text-muted py-4">ยังไม่มีประวัติการลา</td></tr>
                            ) : leaveHistory.map(r => {
                              const ls = LEAVE_STATUS[r.lr_status] || LEAVE_STATUS.pending
                              return (
                                <tr key={r.lr_id}>
                                  <td>{r.lt_name}</td>
                                  <td>{fmtDateTH(r.lr_start_date)} - {fmtDateTH(r.lr_end_date)}</td>
                                  <td className="text-end">{fmtDays(r.lr_days)}</td>
                                  <td className="text-truncate" style={{ maxWidth: 200 }}>{r.lr_reason || '-'}</td>
                                  <td><Badge bg={ls.bg}>{ls.label}</Badge></td>
                                  <td>{r.approved_by_name || '-'}</td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </Table>
                      </Tab.Pane>
                    </Tab.Content>
                  </Card.Body>
                </Card>
              </Tab.Container>
            </>
          )}
        </Container>
      </div>
      <Footer />

      {/* Education modal */}
      <Modal show={!!eduModal} onHide={() => setEduModal(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">{eduModal?.he_id ? 'แก้ไข' : 'เพิ่ม'}วุฒิการศึกษา</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveEducation}>
          <Modal.Body>
            <Row className="g-2">
              <Col xs={6}>
                <Form.Label className="fs-sm mb-1">ระดับ</Form.Label>
                <Form.Control size="sm" placeholder="เช่น ปริญญาตรี" value={eduModal?.he_level || ''}
                  onChange={e => setEduModal(m => ({ ...m, he_level: e.target.value }))} />
              </Col>
              <Col xs={6}>
                <Form.Label className="fs-sm mb-1">วุฒิ/ปริญญา</Form.Label>
                <Form.Control size="sm" placeholder="เช่น ค.บ." value={eduModal?.he_degree || ''}
                  onChange={e => setEduModal(m => ({ ...m, he_degree: e.target.value }))} />
              </Col>
              <Col xs={6}>
                <Form.Label className="fs-sm mb-1">สาขา</Form.Label>
                <Form.Control size="sm" value={eduModal?.he_major || ''}
                  onChange={e => setEduModal(m => ({ ...m, he_major: e.target.value }))} />
              </Col>
              <Col xs={6}>
                <Form.Label className="fs-sm mb-1">ปีที่จบ (พ.ศ.)</Form.Label>
                <Form.Control size="sm" value={eduModal?.he_grad_year || ''}
                  onChange={e => setEduModal(m => ({ ...m, he_grad_year: e.target.value }))} />
              </Col>
              <Col xs={12}>
                <Form.Label className="fs-sm mb-1">สถาบัน</Form.Label>
                <Form.Control size="sm" value={eduModal?.he_institution || ''}
                  onChange={e => setEduModal(m => ({ ...m, he_institution: e.target.value }))} />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" size="sm" onClick={() => setEduModal(null)}>ยกเลิก</Button>
            <Button variant="primary" size="sm" type="submit" disabled={savingItem}>
              {savingItem ? <Spinner size="sm" className="me-1" /> : <i className="ri-save-line me-1" />}บันทึก
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Work-history modal */}
      <Modal show={!!workModal} onHide={() => setWorkModal(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">{workModal?.hw_id ? 'แก้ไข' : 'เพิ่ม'}ประวัติการทำงาน</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveWork}>
          <Modal.Body>
            <Row className="g-2">
              <Col xs={6}>
                <Form.Label className="fs-sm mb-1">ตำแหน่ง</Form.Label>
                <Form.Control size="sm" value={workModal?.hw_position || ''}
                  onChange={e => setWorkModal(m => ({ ...m, hw_position: e.target.value }))} />
              </Col>
              <Col xs={6}>
                <Form.Label className="fs-sm mb-1">หน่วยงาน</Form.Label>
                <Form.Control size="sm" value={workModal?.hw_organization || ''}
                  onChange={e => setWorkModal(m => ({ ...m, hw_organization: e.target.value }))} />
              </Col>
              <Col xs={6}>
                <Form.Label className="fs-sm mb-1">วันที่เริ่ม</Form.Label>
                <Form.Control size="sm" type="date" value={workModal?.hw_start_date || ''}
                  onChange={e => setWorkModal(m => ({ ...m, hw_start_date: e.target.value }))} />
              </Col>
              <Col xs={6}>
                <Form.Label className="fs-sm mb-1">วันที่สิ้นสุด <span className="text-muted">(เว้นว่าง = ปัจจุบัน)</span></Form.Label>
                <Form.Control size="sm" type="date" value={workModal?.hw_end_date || ''}
                  onChange={e => setWorkModal(m => ({ ...m, hw_end_date: e.target.value }))} />
              </Col>
              <Col xs={12}>
                <Form.Label className="fs-sm mb-1">หมายเหตุ</Form.Label>
                <Form.Control size="sm" as="textarea" rows={2} value={workModal?.hw_note || ''}
                  onChange={e => setWorkModal(m => ({ ...m, hw_note: e.target.value }))} />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" size="sm" onClick={() => setWorkModal(null)}>ยกเลิก</Button>
            <Button variant="primary" size="sm" type="submit" disabled={savingItem}>
              {savingItem ? <Spinner size="sm" className="me-1" /> : <i className="ri-save-line me-1" />}บันทึก
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  )
}
